/**
 * PresenceSimplePage — page Présence pour les briques SANS affectation de tâches
 * (urbanisme, comptabilité). Réutilise le composant unifié PlanningPresence
 * (équipe / personne / année + compteurs + absences catégorisées) et fournit,
 * pour les gestionnaires, un bouton « Profils & génération » et « Transmettre à
 * la paie ». Aucun planning de travail / affectation.
 *
 * Paramétré par un client `planningApi` (api.urbanisme.planning ou
 * api.comptabilite.planning) et un accent de module.
 */
import { useState, useEffect, useMemo } from 'react';
import { Send, GripVertical, FileSpreadsheet, FileText } from 'lucide-react';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, type DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { useAuth } from '@/lib/auth-context';
import { api, downloadFichier, type SimpleGrilleCreneau, type SimpleCreneauInput, type SimplePoste } from '@/lib/api';
import { Button, Alert, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/Toast';
import { PlanningPresence, type PlanningAccent } from '@/components/planning/PlanningPresence';
import { PlanningGrilleUnifiee, type CreneauGrille } from '@/components/planning/PlanningGrilleUnifiee';
import { PlanningVueEquipe } from '@/components/planning/PlanningVueEquipe';
import { CreneauEditModal, type CreneauEditValues } from '@/components/planning/CreneauEditModal';
import { PostesManagerUnifie, type PostesPlanningApi } from '@/components/planning/PostesManagerUnifie';
import { interpretPlanningDrop } from '@/components/planning/planning-drag';
import { DeclarerAbsenceModal } from '@/pages/service-technique/DeclarerAbsenceModal';
import { BoutonCloturePeriode } from '@/components/planning/BoutonCloturePeriode';
import type { PlanningWeekResponse, PlanningAnneeResponse, ProfilPlage } from '@roronoa-collectivites/shared-types';
const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const JOURS_LONG = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export interface SimplePlanningApi {
  presenceSemaine: (weekStart: string) => Promise<PlanningWeekResponse>;
  presenceAnnee: (annee: number, agentId?: string) => Promise<PlanningAnneeResponse>;
  listAgents: () => Promise<{ items: { id: string; fullName: string; role: string; couleurMarker: string | null }[] }>;
  setAgentCouleur?: (agentUserId: string, couleurMarker: string | null) => Promise<void>;
  listProfils: () => Promise<{ id: string; nom: string; type: string; plages: ProfilPlage[] }[]>;
  // `type` est optionnel dans le contrat commun : certaines briques (scolaire)
  // exigent un type de profil, les autres n'en ont pas. Le rendre optionnel ici
  // permet à toutes de partager le même composant sans le dupliquer.
  createProfil: (body: { nom: string; type?: string; plages: ProfilPlage[] }) => Promise<{ id: string; nom: string; type: string; plages: ProfilPlage[] }>;
  deleteProfil: (id: string) => Promise<void>;
  generer: (body: { profilId: string; agentIds: string[]; from: string; to: string; inclureVacances?: boolean }) => Promise<{ creneauxCrees: number; joursIgnores: number }>;
  transmettrePaie: (from: string, to: string) => Promise<{ transmis: number }>;
  // Grille unique (livraison B)
  getWeek: (from: string, to: string) => Promise<SimpleGrilleCreneau[]>;
  getMaSemaine: (from: string, to: string) => Promise<SimpleGrilleCreneau[]>;
  joursNonTravailles: (from: string, to: string) => Promise<{ date: string; libelle: string }[]>;
  createCreneau: (body: SimpleCreneauInput) => Promise<SimpleGrilleCreneau>;
  updateCreneau: (id: string, body: Partial<SimpleCreneauInput>) => Promise<SimpleGrilleCreneau>;
  deleteCreneau: (id: string) => Promise<void>;
  listPostes: () => Promise<SimplePoste[]>;
  createPoste: (body: { titre: string; dureeMin?: number; couleur?: string }) => Promise<SimplePoste>;
  deletePoste: (id: string) => Promise<void>;
}

function ymd(d: Date): string { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function startOfWeek(d = new Date()): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); const dow = x.getDay() === 0 ? 7 : x.getDay(); x.setDate(x.getDate() - (dow - 1)); return x; }
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export function PresenceSimplePage({ planningApi, accent, service }: {
  planningApi: SimplePlanningApi;
  accent: PlanningAccent;
  service: string;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const peutGerer = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';
  const isAgent = user?.role === 'agent';
  // Vue par défaut : l'agent arrive sur SON planning (c'est ce qu'il vient
  // consulter) ; l'encadrement arrive sur l'équipe (c'est ce qu'il pilote).
  const [vue, setVue] = useState<'equipe' | 'individuelle'>(
    user?.role === 'agent' ? 'individuelle' : 'equipe',
  );
  // Préfixe d'URL de la brique. Il ne se déduit pas du nom de service : le
  // scolaire s'appelle « scolaire_cantine » côté permissions mais « scolaire »
  // dans les URL, et la comptabilité « comptable » / « comptabilite ».
  const apiBase = ({
    scolaire_cantine: 'scolaire',
    comptable: 'comptabilite',
    petite_enfance: 'petite-enfance',
    urbanisme: 'urbanisme',
    grc: 'grc',
  } as Record<string, string>)[service] ?? service;

  // Export : portée (semaine affichée ou mois qui la contient) + état de chargement.
  const [porteeExport, setPorteeExport] = useState<'semaine' | 'mois'>('semaine');
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [absModal, setAbsModal] = useState<{ agent: { id: string; fullName: string }; jourISO: string } | null>(null);
  const [profilsOpen, setProfilsOpen] = useState(false);
  const [postesOpen, setPostesOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Grille unique : créneaux + postes glissables (comme GRC/ST/scolaire).
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek());
  const [creneaux, setCreneaux] = useState<SimpleGrilleCreneau[]>([]);
  const [agents, setAgents] = useState<{ id: string; fullName: string; role: string; couleurMarker: string | null }[]>([]);
  const [postes, setPostes] = useState<SimplePoste[]>([]);
  const [nonTravailles, setNonTravailles] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [dragPoste, setDragPoste] = useState<SimplePoste | null>(null);
  const [affectPoste, setAffectPoste] = useState<{ poste: SimplePoste; jour: Date } | null>(null);
  const [editCreneau, setEditCreneau] = useState<SimpleGrilleCreneau | null>(null);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  async function load() {
    setLoading(true);
    try {
      const from = ymd(weekStart);
      const to = ymd(addDays(weekStart, 7));
      const [week, ag, jnt, pst] = await Promise.all([
        // Le chargement suit la VUE, plus le rôle : un agent en vue « Équipe » a
        // besoin des créneaux de tout le service, pas seulement des siens.
        // `getWeek` applique le périmètre côté serveur (chargerAgentsVisibles),
        // donc un agent n'obtient jamais que son propre service.
        vue === 'equipe' ? planningApi.getWeek(from, to) : planningApi.getMaSemaine(from, to),
        planningApi.listAgents().catch(() => ({ items: [] })),
        planningApi.joursNonTravailles(from, to).catch(() => []),
        planningApi.listPostes().catch(() => []),
      ]);
      setCreneaux(week); setAgents(ag.items);
      setNonTravailles(new Map(jnt.map((j) => [j.date, j.libelle])));
      setPostes(pst);
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [weekStart, vue]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  function onDragEnd(event: DragEndEvent) {
    setDragPoste(null);
    const res = interpretPlanningDrop<SimplePoste>(event, weekDays);
    if (!res) return;
    if (res.kind === 'move') {
      void (async () => {
        try {
          await planningApi.updateCreneau(res.creneau.id, {
            dateDebut: res.nouveauDebut.toISOString(),
            dateFin: res.nouvelleFin.toISOString(),
          });
          await load();
        } catch (e) { setError(e instanceof Error ? e.message : 'Erreur lors du déplacement'); }
      })();
      return;
    }
    if (vue === 'individuelle' && agentIndividuel) {
      const dureeMin = res.poste.dureeMin ?? 120;
      const fin = new Date(res.jour.getTime() + dureeMin * 60000);
      void (async () => {
        try {
          await planningApi.createCreneau({
            agentUserId: agentIndividuel,
            dateDebut: res.jour.toISOString(),
            dateFin: fin.toISOString(),
            titre: res.poste.titre,
            type: 'presence',
            couleurMarker: res.poste.couleur ?? null,
          });
          await load();
        } catch (e) { setError(e instanceof Error ? e.message : 'Erreur lors de l\'affectation'); }
      })();
      return;
    }
    setAffectPoste({ poste: res.poste, jour: res.jour });
  }

  const creneauxGrille: CreneauGrille[] = creneaux.map((c) => ({
    id: c.id, agentUserId: c.agentUserId, agentFullName: c.agentNom ?? '—', agentCouleurMarker: null,
    dateDebut: c.dateDebut, dateFin: c.dateFin, titre: c.titre, description: c.description,
    signalementId: null, signalementNumero: null, type: c.type, couleurMarker: c.couleurMarker,
  }));
  const days = weekDays.map((d) => ({ label: JOURS_LONG[d.getDay() === 0 ? 6 : d.getDay() - 1]!, date: d }));
  const todayKey = new Date().toDateString();
  const colorAgentById = new Map(agents.map((a) => [a.id, a.couleurMarker ?? null]));
  const colorOf = (c: CreneauGrille): string => {
    if (creneaux.find((x) => x.id === c.id)?.statut === 'absent') return '#b91c1c';
    return c.couleurMarker ?? colorAgentById.get(c.agentUserId) ?? '#64748b';
  };

  // ── Refonte planning 21/07 : vue Équipe (défaut) / Individuelle + modale unifiée
  const [agentIndividuel, setAgentIndividuel] = useState<string>('');
  useEffect(() => {
    if (!agentIndividuel && agents.length > 0) setAgentIndividuel(agents[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents]);
  const agentsVueEquipe = agents.map((a) => ({ id: a.id, fullName: a.fullName, couleurMarker: a.couleurMarker ?? null }));
  const setCouleurAgent = planningApi.setAgentCouleur
    ? async (agentId: string, couleur: string | null) => {
        try { await planningApi.setAgentCouleur!(agentId, couleur); await load(); }
        catch (e) { setError(e instanceof Error ? e.message : 'Erreur couleur'); }
      }
    : undefined;
  const btnVariant = accent === 'urbanisme' ? 'bg-urba' : accent === 'comptable' ? 'bg-compta' : 'bg-tech';

  async function submitEdit(v: CreneauEditValues) {
    if (!editCreneau) return;
    await planningApi.updateCreneau(editCreneau.id, {
      agentUserId: v.agentUserId, dateDebut: v.dateDebut, dateFin: v.dateFin,
      titre: v.titre, type: v.type || 'presence', description: v.description ?? undefined,
    });
    setEditCreneau(null);
    await load();
  }
  async function deleteEdit() {
    if (!editCreneau) return;
    await planningApi.deleteCreneau(editCreneau.id);
    setEditCreneau(null);
    await load();
  }

  const postesApiSimple: PostesPlanningApi = {
    listPostes: () => planningApi.listPostes().then((ps) => ps.map((p) => ({ id: p.id, titre: p.titre, dureeMin: p.dureeMin, couleur: p.couleur ?? null }))),
    createPoste: (b) => planningApi.createPoste(b).then((p) => ({ id: p.id, titre: p.titre, dureeMin: p.dureeMin, couleur: p.couleur ?? null })),
    deletePoste: (id) => planningApi.deletePoste(id),
    createCreneau: (b) => planningApi.createCreneau({ agentUserId: b.agentUserId, dateDebut: b.dateDebut, dateFin: b.dateFin, titre: b.titre, type: b.type, couleurMarker: b.couleurMarker ?? null }),
  };

  /**
   * Télécharge le planning AFFICHÉ.
   *
   * Règle : l'export reflète l'écran. En vue individuelle, on ne sort que
   * l'agent sélectionné ; en vue équipe, tous les agents visibles. La portée
   * suit le sélecteur Semaine/Mois. Aucun filtrage supplémentaire.
   */
  async function doExport(kind: 'excel' | 'pdf') {
    setExporting(kind); setError(null);
    try {
      const cible = vue === 'individuelle' && agentIndividuel ? agentIndividuel : undefined;
      const qs = new URLSearchParams({ date: ymd(weekStart), portee: porteeExport });
      if (cible) qs.set('agentId', cible);
      const ext = kind === 'excel' ? 'xlsx' : 'pdf';
      await downloadFichier(
        `/api/${apiBase}/planning/export/${kind}?${qs.toString()}`,
        `planning-${service}-${porteeExport}.${ext}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export impossible');
    } finally { setExporting(null); }
  }

  async function transmettrePaie() {
    const now = new Date();
    const debutMois = new Date(now.getFullYear(), now.getMonth(), 1);
    const finMois = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    try {
      const r = await planningApi.transmettrePaie(ymd(debutMois), ymd(finMois));
      toast.success(`${r.transmis} créneau(x) du mois transmis à la paie.`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur'); }
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold text-ink">{vue === 'equipe' ? 'Planning de l\'équipe' : 'Mon planning'}</h1>
          {/*
            Bascule ouverte à TOUS les rôles, agent compris (retour mairie 24/07).
            Un agent doit pouvoir voir qui est présent dans son service sans aller
            chercher ailleurs — c'est le geste de consultation rapide, distinct de
            l'espace RH où il gère SON planning (semaine/année, absences, export).

            Le cloisonnement n'est pas affaibli : la liste des agents vient de
            `chargerAgentsVisibles`, qui applique le périmètre côté serveur. Un
            agent ne voit que son propre service.
          */}
          <div className="inline-flex rounded-r-lg border border-border-hi bg-surface/60 p-0.5">
            <button type="button" onClick={() => setVue('equipe')} className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'equipe' ? `${btnVariant} text-white` : 'text-ink-muted'}`}>Équipe</button>
            <button type="button" onClick={() => setVue('individuelle')} className={`rounded-r-md px-3 py-1.5 text-sm ${vue === 'individuelle' ? `${btnVariant} text-white` : 'text-ink-muted'}`}>Individuelle</button>
          </div>
        </div>
        {peutGerer && (
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setPostesOpen(true)}>Postes</Button>
            <Button variant="secondary" size="sm" onClick={() => setProfilsOpen(true)}>Profils &amp; génération</Button>
            <Button variant="secondary" size="sm" onClick={() => void transmettrePaie()}>
              <Send className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">Transmettre à la paie</span>
            </Button>
            <BoutonCloturePeriode service={service} />
          </div>
        )}
      </div>
      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, -7)))}>← Semaine préc.</Button>
        <span className="text-sm font-medium text-ink">Semaine du {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
        <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(addDays(weekStart, 7)))}>Semaine suiv. →</Button>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/*
            Portée de l'EXPORT. L'écran reste hebdomadaire ; ce sélecteur dit si
            le document sort la semaine affichée ou le mois qui la contient.
            Il est volontairement collé aux boutons d'export pour qu'on lise
            « Semaine | Mois → Excel | PDF » d'un seul tenant.
          */}
          <div className="inline-flex rounded-r-lg border border-border-hi bg-surface/60 p-0.5">
            <button
              type="button" onClick={() => setPorteeExport('semaine')}
              className={`rounded-r-md px-2.5 py-1 text-xs ${porteeExport === 'semaine' ? `${btnVariant} text-white` : 'text-ink-muted'}`}
            >Semaine</button>
            <button
              type="button" onClick={() => setPorteeExport('mois')}
              className={`rounded-r-md px-2.5 py-1 text-xs ${porteeExport === 'mois' ? `${btnVariant} text-white` : 'text-ink-muted'}`}
            >Mois</button>
          </div>
          <Button variant="secondary" size="sm" onClick={() => void doExport('excel')} disabled={exporting !== null}>
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
            {exporting === 'excel' ? 'Export…' : 'Excel'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void doExport('pdf')} disabled={exporting !== null}>
            <FileText className="h-4 w-4" aria-hidden="true" />
            {exporting === 'pdf' ? 'Export…' : 'PDF'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : vue === 'equipe' ? (
        /* VUE ÉQUIPE (défaut) — 1 ligne/agent, cadres colorés alignés */
        <PlanningVueEquipe
          days={days}
          agents={agentsVueEquipe}
          creneaux={creneauxGrille}
          colorOfCreneau={colorOf}
          onSetAgentColor={peutGerer ? setCouleurAgent : undefined}
          onCreneauClick={(c) => { const orig = creneaux.find((x) => x.id === c.id); if (orig && peutGerer) setEditCreneau(orig); }}
          /* LOT 2 — cette page sert URBANISME et COMPTABILITÉ (écran partagé,
             paramétré par planningApi). Elle a le même code mort que les autres :
             le modal était rendu sans jamais être ouvert depuis la grille. */
          onDeclarerAbsence={peutGerer ? (agent, jourISO) =>
            setAbsModal({ agent: { id: agent.id, fullName: agent.fullName }, jourISO }) : undefined}
        />
      ) : (
        /* VUE INDIVIDUELLE — grille horaire d'UN agent (glisser un poste pour créer) */
        <DndContext sensors={sensors} onDragStart={(e) => { const d = e.active.data.current as { poste?: SimplePoste } | undefined; setDragPoste(d?.poste ?? null); }} onDragEnd={onDragEnd}>
          {!isAgent && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Agent :</label>
              <select value={agentIndividuel} onChange={(e) => setAgentIndividuel(e.target.value)} className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                {agents.map((a) => <option key={a.id} value={a.id}>{a.fullName}</option>)}
              </select>
            </div>
          )}
          {!isAgent && postes.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-r-lg border border-border bg-surface/30 p-2.5">
              <span className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Postes à glisser :</span>
              {postes.map((p) => <SimplePosteChip key={p.id} poste={p} accent={accent} />)}
              <button type="button" onClick={() => setPostesOpen(true)} className="ml-auto text-xs text-ink-muted hover:underline">Gérer</button>
            </div>
          )}
          <PlanningGrilleUnifiee
            accent={accent}
            days={days}
            todayKey={todayKey}
            creneaux={creneauxGrille}
            filterAgentId={isAgent ? undefined : agentIndividuel}
            onCreneauClick={(c) => { const orig = creneaux.find((x) => x.id === c.id); if (orig && peutGerer) setEditCreneau(orig); }}
            colorOfCreneau={colorOf}
            nonTravailles={nonTravailles}
          />
          <DragOverlay>
            {dragPoste && (
              <div className="rounded-r-pill border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-ink shadow-pop">{dragPoste.titre}</div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {absModal && (
        <DeclarerAbsenceModal
          agent={absModal.agent}
          jourISO={absModal.jourISO}
          service={service}
          onClose={() => setAbsModal(null)}
          onDone={(conflits) => {
            setAbsModal(null);
            void load();
            toast.success(conflits > 0
              ? `Absence enregistrée. ${conflits} tâche(s) à réaffecter — responsable alerté.`
              : 'Absence enregistrée.');
          }}
        />
      )}

      {affectPoste && (
        <AffectSimpleDialog
          poste={affectPoste.poste}
          jour={affectPoste.jour}
          agents={agents}
          accent={accent}
          planningApi={planningApi}
          onClose={() => setAffectPoste(null)}
          onDone={() => { setAffectPoste(null); void load(); }}
        />
      )}

      {editCreneau && (
        <CreneauEditModal
          mode="edit"
          accent={accent}
          initial={{
            id: editCreneau.id, agentUserId: editCreneau.agentUserId, titre: editCreneau.titre,
            description: editCreneau.description, type: editCreneau.type,
            dateDebut: editCreneau.dateDebut, dateFin: editCreneau.dateFin,
          }}
          agents={agents}
          types={[]}
          onClose={() => setEditCreneau(null)}
          onSubmit={submitEdit}
          onDelete={deleteEdit}
        />
      )}

      {profilsOpen && (
        <ProfilsSimpleDialog
          planningApi={planningApi}
          accent={accent}
          onClose={() => setProfilsOpen(false)}
          onDone={() => { setProfilsOpen(false); void load(); }}
        />
      )}

      <PostesManagerUnifie
        open={postesOpen}
        onClose={() => setPostesOpen(false)}
        agents={agents}
        planningApi={postesApiSimple}
        accent={accent}
        typeCreneau="presence"
        titre="Postes"
        onChanged={() => void load()}
      />
    </div>
  );
}

function ProfilsSimpleDialog({ planningApi, accent, onClose, onDone }: {
  planningApi: SimplePlanningApi;
  accent: PlanningAccent;
  onClose: () => void;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [profils, setProfils] = useState<{ id: string; nom: string; plages: ProfilPlage[] }[]>([]);
  const [agents, setAgents] = useState<{ id: string; fullName: string; role: string; couleurMarker: string | null }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // nouveau profil
  const [nom, setNom] = useState('');
  const [jour, setJour] = useState(1);
  const [debut, setDebut] = useState('09:00');
  const [fin, setFin] = useState('17:00');
  const [plages, setPlages] = useState<ProfilPlage[]>([]);
  // génération
  const [profilId, setProfilId] = useState('');
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useState(() => {
    void planningApi.listProfils().then(setProfils).catch(() => setProfils([]));
    void planningApi.listAgents().then((r) => setAgents(r.items)).catch(() => setAgents([]));
    return undefined;
  });

  async function creerProfil() {
    if (nom.trim().length < 2 || plages.length === 0) { setError('Nom + au moins une plage'); return; }
    setError(null);
    try {
      const p = await planningApi.createProfil({ nom: nom.trim(), plages });
      setProfils((prev) => [...prev, p]); setNom(''); setPlages([]); setMsg('Profil créé.');
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur'); }
  }

  async function generer() {
    if (!profilId || agentIds.length === 0 || !from || !to) { setError('Profil, agents et période requis'); return; }
    setError(null); setMsg(null);
    try {
      const r = await planningApi.generer({ profilId, agentIds, from, to });
      toast.success(`${r.creneauxCrees} créneau(x) généré(s).`);
      onDone();
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur'); }
  }

  const accentUnused = accent; void accentUnused;

  return (
    <Dialog open onClose={onClose} title="Profils de présence &amp; génération" size="lg">
      <div className="flex flex-col gap-5">
        {error && <Alert variant="error">{error}</Alert>}
        {msg && <Alert variant="success">{msg}</Alert>}

        <div className="rounded-r-lg border border-border bg-surface/40 p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink">Nouveau profil</h3>
          <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom du profil" className="col-span-2 h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink sm:col-span-1" />
            <select value={jour} onChange={(e) => setJour(Number(e.target.value))} className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
              {JOURS.map((j, i) => <option key={i} value={i + 1}>{j}</option>)}
            </select>
            <input type="time" value={debut} onChange={(e) => setDebut(e.target.value)} className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            <input type="time" value={fin} onChange={(e) => setFin(e.target.value)} className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
          </div>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {plages.map((pl, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-r-pill bg-surface/70 px-2 py-0.5 text-xs text-ink-muted">
                {JOURS[pl.jour - 1]} {pl.debut}–{pl.fin}
                <button type="button" onClick={() => setPlages((p) => p.filter((_, j) => j !== i))} className="text-ink-subtle">×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPlages((p) => [...p, { jour, debut, fin }])}>+ Plage</Button>
            <Button variant="secondary" size="sm" onClick={() => void creerProfil()}>Enregistrer le profil</Button>
          </div>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-ink">Générer sur une période</h3>
          <Field label="Profil" htmlFor="gs-profil" required>
            <select id="gs-profil" value={profilId} onChange={(e) => setProfilId(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
              <option value="">— Choisir —</option>
              {profils.map((p) => <option key={p.id} value={p.id}>{p.nom} · {p.plages.length} plage(s)</option>)}
            </select>
          </Field>
          <div className="my-2">
            <span className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Agents</span>
            <div className="flex flex-wrap gap-1.5">
              {agents.map((a) => {
                const on = agentIds.includes(a.id);
                return (
                  <button key={a.id} type="button" onClick={() => setAgentIds((prev) => on ? prev.filter((x) => x !== a.id) : [...prev, a.id])}
                    className={['rounded-r-pill border px-2.5 py-1 text-xs', on ? 'border-ink bg-surface/80 text-ink' : 'border-border text-ink-muted'].join(' ')}>
                    {a.fullName}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Du" htmlFor="gs-from" required><input id="gs-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" /></Field>
            <Field label="Au" htmlFor="gs-to" required><input id="gs-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" /></Field>
          </div>
          <div className="mt-3 flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => void generer()}>Générer le planning</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

// ── Poste glissable (chip) ────────────────────────────────────────────────────
function SimplePosteChip({ poste, accent }: { poste: SimplePoste; accent: PlanningAccent }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `poste-${poste.id}`, data: { poste } });
  return (
    <button ref={setNodeRef} {...listeners} {...attributes} type="button"
      className="inline-flex cursor-grab items-center gap-1 rounded-r-pill border border-border bg-surface/70 px-2.5 py-1 text-xs font-semibold text-ink active:cursor-grabbing"
      style={{ opacity: isDragging ? 0.4 : 1, borderColor: poste.couleur ?? undefined }}>
      <GripVertical className="h-3 w-3 opacity-60" />
      {poste.titre}
    </button>
  );
}

// ── Affectation d'un poste déposé (personne + heures), heure du drop pré-remplie ─
function AffectSimpleDialog({ poste, jour, agents, accent, planningApi, onClose, onDone }: {
  poste: SimplePoste; jour: Date; agents: { id: string; fullName: string; role: string }[];
  accent: PlanningAccent; planningApi: SimplePlanningApi; onClose: () => void; onDone: () => void;
}) {
  const heureDrop = jour.getHours() > 0 ? `${String(jour.getHours()).padStart(2, '0')}:00` : '09:00';
  const finDrop = new Date(jour.getTime() + (poste.dureeMin || 120) * 60000);
  const [agentId, setAgentId] = useState('');
  const [debut, setDebut] = useState(heureDrop);
  const [fin, setFin] = useState(`${String(finDrop.getHours()).padStart(2, '0')}:${String(finDrop.getMinutes()).padStart(2, '0')}`);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function affecter() {
    if (!agentId) { setError('Choisir une personne'); return; }
    setSaving(true); setError(null);
    try {
      const j = ymd(jour);
      const body: SimpleCreneauInput = {
        agentUserId: agentId,
        dateDebut: new Date(`${j}T${debut}:00`).toISOString(),
        dateFin: new Date(`${j}T${fin}:00`).toISOString(),
        titre: poste.titre, type: poste.type ?? 'presence',
        couleurMarker: poste.couleur ?? null,
      };
      await planningApi.createCreneau(body);
      onDone();
    } catch (e) { setError(e instanceof Error ? e.message : 'Erreur'); setSaving(false); }
  }

  return (
    <Dialog open onClose={onClose} title={`Affecter — ${poste.titre}`}>
      <div className="flex flex-col gap-3.5">
        <p className="text-xs text-ink-muted">{jour.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        {error && <Alert variant="error">{error}</Alert>}
        <Field label="Personne" htmlFor="asd-agent" required>
          <select id="asd-agent" value={agentId} onChange={(e) => setAgentId(e.target.value)} className="h-11 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink">
            <option value="">—</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.fullName}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Début" htmlFor="asd-debut"><Input id="asd-debut" type="time" value={debut} onChange={(e) => setDebut(e.target.value)} /></Field>
          <Field label="Fin" htmlFor="asd-fin"><Input id="asd-fin" type="time" value={fin} onChange={(e) => setFin(e.target.value)} /></Field>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant={accent === 'urbanisme' ? 'urba' : accent === 'comptable' ? 'compta' : 'primary'} loading={saving} onClick={() => void affecter()}>Affecter</Button>
        </div>
      </div>
    </Dialog>
  );
}

